import EditorialAccessErrorScreen from 'app/components/EditorialAccessBoundary/EditorialAccessErrorScreen';
import Loading from 'app/components/Loading/LoadingScreen';
import { useEditorialAccess } from 'app/context/editorialAccess.context';
import { EditorialResourceContext } from 'app/context/editorialResource.context';
import { EDITORIAL_ACCESS_STATUS, hasRestrictedProgramAccess } from 'app/models/editorialAccess';
import { getEditorialCapabilities, getEditorialRouteAction } from 'app/models/editorialCapabilities';
import { editorialProgramRoute, PROGRAM_ACCESS_REVOKED_EVENT, PROGRAM_ROUTE_TYPE } from 'app/models/editorialProgramRoute';
import { USER_ROLES } from 'app/models/user/user.constants';
import { canOpenEditorialContent, canOpenEditorialProgram } from 'app/services/editorialPrograms.service';
import { pauseEditorialRequestScope, publishEditorialRequestScope, releaseEditorialRequestScope } from 'app/config/editorialRequestSession.config';
import PropTypes from 'prop-types';
import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';

const PROGRAM_ACCESS_STATUS = { allowed: 'allowed', denied: 'denied', bypass: 'bypass', ...EDITORIAL_ACCESS_STATUS };
const REVALIDATION_EVENTS = ['focus', PROGRAM_ACCESS_REVOKED_EVENT];

function isRestricted(access) {
  return hasRestrictedProgramAccess(access, { role_guid: access.role }) || (access.role === USER_ROLES.publisher && access.data?.restricted === true);
}

function isEnforcementReady(access) {
  return access.data?.feature_enabled && access.data?.program_access_enforced && (access.data?.access_level !== 'reviewer' || access.data?.reviewer_write_enforced);
}

function isReviewerWriteRoute(access, pathname) {
  return getEditorialRouteAction(pathname) === 'write' && access.data?.access_level === 'reviewer';
}

function resolveBoundaryStatus({ restricted, route, pathname, state, enforcementReady, access, capabilities }) {
  if (!restricted || route.type === PROGRAM_ROUTE_TYPE.catalog) return PROGRAM_ACCESS_STATUS.bypass;
  if (!enforcementReady) return PROGRAM_ACCESS_STATUS.denied;
  if (isReviewerWriteRoute(access, pathname)) return PROGRAM_ACCESS_STATUS.denied;
  if (route.type === PROGRAM_ROUTE_TYPE.unknown) return PROGRAM_ACCESS_STATUS.denied;
  const current = state.guid === route.guid && state.pathname === pathname;
  if (!current) return PROGRAM_ACCESS_STATUS.loading;
  if (state.status === PROGRAM_ACCESS_STATUS.denied || state.status === PROGRAM_ACCESS_STATUS.error) return state.status;
  if (state.status !== PROGRAM_ACCESS_STATUS.allowed) return PROGRAM_ACCESS_STATUS.loading;
  if (!capabilities.canRead) return PROGRAM_ACCESS_STATUS.denied;
  return PROGRAM_ACCESS_STATUS.allowed;
}

export default function ProgramAccessBoundary({ children }) {
  const requestOwner = React.useRef(Symbol('editorial-resource'));
  const access = useEditorialAccess();
  const restricted = isRestricted(access);
  const enforcementReady = isEnforcementReady(access);
  const { pathname } = useLocation();
  const route = editorialProgramRoute(pathname);
  const resourceGuid = route.guid;
  const resourceType = route.type;
  const [state, setState] = React.useState({ status: PROGRAM_ACCESS_STATUS.idle });
  const [attempt, setAttempt] = React.useState(0);
  const retry = React.useCallback(() => {
    pauseEditorialRequestScope(requestOwner.current);
    setState({ status: PROGRAM_ACCESS_STATUS.idle });
    setAttempt((value) => value + 1);
  }, []);

  React.useEffect(() => {
    if (!restricted || !resourceGuid || !enforcementReady || access.revalidating) return undefined;
    const controller = new AbortController();
    let current = true;
    let request = 0;

    // Una revalidación en segundo plano no debe parpadear sobre una vista ya autorizada.
    async function check(event) {
      pauseEditorialRequestScope(requestOwner.current);
      const revision = ++request;
      setState((previous) => {
        const preserveState = previous.guid === resourceGuid && previous.pathname === pathname && previous.status === PROGRAM_ACCESS_STATUS.allowed;
        if (preserveState) return { ...previous, revalidating: true };
        return { status: PROGRAM_ACCESS_STATUS.loading, guid: resourceGuid, pathname };
      });
      try {
        const allowed = await (resourceType === PROGRAM_ROUTE_TYPE.content ? canOpenEditorialContent : canOpenEditorialProgram)(resourceGuid, controller.signal);
        let status = PROGRAM_ACCESS_STATUS.error;
        if (allowed === true) status = PROGRAM_ACCESS_STATUS.allowed;
        if (allowed === false) status = PROGRAM_ACCESS_STATUS.denied;
        if (current && revision === request) setState({ status, guid: resourceGuid, pathname });
      } catch {
        if (current && revision === request) setState({ status: PROGRAM_ACCESS_STATUS.error, guid: resourceGuid, pathname });
      }
    }

    check();
    REVALIDATION_EVENTS.forEach((event) => globalThis.addEventListener(event, check));

    return () => {
      current = false;
      controller.abort();
      REVALIDATION_EVENTS.forEach((event) => globalThis.removeEventListener(event, check));
    };
  }, [restricted, resourceGuid, resourceType, pathname, attempt, enforcementReady, access.revalidating]);

  const resourceStatus = state.guid === resourceGuid && state.pathname === pathname ? state.status : PROGRAM_ACCESS_STATUS.loading;
  const resources = React.useMemo(
    () => [{ type: resourceType, guid: resourceGuid, status: resourceStatus, revalidating: state.revalidating === true }],
    [resourceType, resourceGuid, resourceStatus, state.revalidating]
  );
  React.useLayoutEffect(() => {
    publishEditorialRequestScope(requestOwner.current, resources);
  }, [resources]);
  React.useLayoutEffect(() => {
    const owner = requestOwner.current;
    return () => releaseEditorialRequestScope(owner);
  }, []);

  const capabilities = getEditorialCapabilities(access, resources);
  const boundaryStatus = resolveBoundaryStatus({ restricted, route, pathname, state, enforcementReady, access, capabilities });
  if (boundaryStatus === PROGRAM_ACCESS_STATUS.bypass) return children;
  if (boundaryStatus === PROGRAM_ACCESS_STATUS.denied) return <Redirect to="/programs" />;
  if (boundaryStatus === PROGRAM_ACCESS_STATUS.error) return <EditorialAccessErrorScreen onRetry={retry} />;
  if (boundaryStatus === PROGRAM_ACCESS_STATUS.loading) return <Loading />;

  // Solo se anuncia el trabajo en curso. Inertizar el subárbol durante la revalidación mataba el foco,
  // dejaba ESC sin efecto dentro de los diálogos y escondía el propio `aria-busy` del árbol de
  // accesibilidad, además de contradecir a `writePaused`: la vista sigue usable y es la autorización de
  // cada escritura la que decide.
  const revalidating = access.revalidating || state.revalidating;
  return (
    <EditorialResourceContext.Provider value={resources}>
      <div aria-busy={revalidating || undefined}>{children}</div>
    </EditorialResourceContext.Provider>
  );
}
ProgramAccessBoundary.propTypes = { children: PropTypes.node };
